import {
  consoleExport,
  createRootTel,
  createRouteTel,
  type RootTel,
  type Tel,
} from './index'
import type { TelStorageBindable } from './tel-storage'
import type { TelStorageConfig } from './tel-storage'

export interface _BrowserClientTel {
  current: Tel
  root: RootTel
  onRouteChange(pathname: string): void
  dispose(): void
}

export interface _InitBrowserClientTelOptions extends Omit<
  TelStorageConfig,
  'errorGroupScope' | 'stackResolver'
> {
  name: string
  env: string
  isSensitive?: (key: string, value: unknown) => boolean
  timeoutMinutes?: number
  handlers?: readonly TelStorageBindable[]
  window?: _BrowserClientTelWindow
}

export interface _BrowserClientTelWindow {
  location: { pathname: string }
  setInterval(handler: () => void, timeoutMs: number): unknown
  clearInterval(intervalId: unknown): void
}

// Internal route lifecycle wiring; application telemetry uses createRootTel.
export function _initBrowserClientTel(
  opts: _InitBrowserClientTelOptions,
): _BrowserClientTel {
  const {
    name,
    env,
    timeoutMinutes = 10,
    handlers = [consoleExport({ minimal: true })],
    window,
    ...storageConfig
  } = opts
  const timeoutMs = timeoutMinutes * 60_000
  const windowTarget = window ?? _globalWindow()

  const root = createRootTel({
    ...storageConfig,
    name,
    origin: 'client',
    env,
  })
  if (handlers.length > 0) {
    root.setHandlers(...handlers)
  }

  let current: Tel = root
  let lastTraceTime = Date.now()

  function rotateTrace(pathname: string) {
    current = createRouteTel(root, current, pathname)
    lastTraceTime = Date.now()
  }

  const intervalId = windowTarget.setInterval(() => {
    if (Date.now() - lastTraceTime >= timeoutMs) {
      rotateTrace(windowTarget.location.pathname)
    }
  }, 60_000)

  return {
    get current() {
      return current
    },
    root,
    onRouteChange(pathname: string) {
      rotateTrace(pathname)
    },
    dispose() {
      windowTarget.clearInterval(intervalId)
    },
  }
}

function _globalWindow(): _BrowserClientTelWindow {
  return (globalThis as typeof globalThis & { window: _BrowserClientTelWindow })
    .window
}
